import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RootState } from '../../../Redux/Store';
import { AppContext } from '../../../Context/AppContext';
import { useAppDispatch, useAppSelector } from '../../../Hooks/Hooks';
import {
  bookLoad,
  getSearchLoads,
  handleResetSearchQueryError,
  updateSearchQuery,
} from '../../../Redux/Reducers/SearchSlice';
import { getAvailableAnchorShipments } from '../../../Redux/Reducers/LoadSlice';
import {
  commonDateFormat,
  dataBaseFriendlyDateFormat,
  dayjs,
  isBeforeCurrentDate,
} from '../../../Utils/DateUtils';
import { getMemberDetails, isAppointmentState, tabFinding } from '../../../Utils/Search';
import { setCookie } from '../../../Utils/CookieUtils';
import { fbLogEvent } from '../../../Helpers/FirebaseHelper';
import {
  ANYWHERE,
  BookingFailed,
  CANCEL_TEXT,
  COOKIE_IDS,
  LAST_SHIPMENT_MODAL_MESSAGE,
  OK_TEXT,
  RefreshResults,
  SERVER_ERROR_MESSAGES,
  UpdateNow,
  dropDateOffset,
  pickupDateOffset,
} from '../../../Constants/Constants';
import { EVENT, SCREEN } from '../../../Constants/AnalyticsConstants';
import { ELoadCardSize } from '../../../Components/Shared/LoadCard/LoadCard.type';
import NewSearchContainer from '../../../Components/Search/NewSearchContainer/NewSearchContainer';
import CustomModal from '../../../Components/Shared/CustomModal/CustomModal';
import FloatingBtnContainer from '../../../Components/Shared/FloatingBtnContainer/FloatingBtnContainer';
import LoadMapContainer from '../../../Components/Shared/LoadCard/ExpandedViewContainer/LoadMapContainer/LoadMapContainer';
import LoadRateContainer from '../../../Components/Shared/LoadCard/ExpandedViewContainer/LoadRateContainer/LoadRateContainer';
import LoadCard from '../../../Components/Shared/LoadCard/LoadCard';
import TermsAndConditions from '../../../Components/Shared/TermsAndConditions/TermsAndConditions';
import LoadReferencesContainer from '../../../Components/Shared/LoadCard/ExpandedViewContainer/LoadReferencesContainer/LoadReferencesContainer';
import './ExpandedResults.scss';
import { isEntitled } from '../../../Utils/ContentOrConfigUtil';

/** ExpandedResults component displays detailed information about a load posting.
 * It includes a map, load card, terms and conditions, and a book now button if applicable.
 * @component
 */
const ExpandedResults = () => {
  let { id, type } = useParams();
  const { isMobile } = useContext(AppContext);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { searchResultData, watchedLoadsData, loadByIdData, sortAndFilter, searchQuery } =
    useAppSelector((state: RootState) => state?.search);
  const { loadDetails } = useAppSelector((state: RootState) => {
    return state?.myLoads;
  });
  const { memberData } = useAppSelector((state: RootState) => state.member);
  const { configTableData } = useAppSelector((state: RootState) => state.ContentOrConfig);
  const appConfigData = configTableData?.FPO_APP_CONFIG;
  const showBanner = useAppSelector((state: RootState) => state.ContentOrConfig?.showBanner);
  const { availableAnchorShipments } = useAppSelector((state: RootState) => state.Load);
  const { lastLocation } = useAppSelector((state: RootState) => state.NatNal);
  const [data, setData] = useState<any>(null);
  const [isMyloads, setMyloads] = useState<boolean>(false);
  const [bookNowModalProps, setBookNowModalProps] = useState({
    isModalOpen: false,
  });
  const [thankYouModalProps, setThankYouModalProps] = useState({
    isModalOpen: false,
  });
  const [lastShipmentWarningModal, setLastShipmentWarningModal] = useState({
    isModalOpen: false,
  });
  const selectedAnchor = useRef<string | null>(null);
  const [insertShipmentModalProps, setInsertShipmentModalProps] = useState<any>({
    isModalOpen: false,
    buttons: [],
  });
  const [warningModalProps, setWarningModalProps] = useState({
    isModalOpen: false,
  });
  const [referencesModalProps, setReferencesModalProps] = useState<any>({
    isModalOpen: false,
  });
  const [isShipmentSelected, setIsShipmentSelected] = useState(false);
  const memberDetails = getMemberDetails(memberData, isMobile);

  const handleShipmentSelection = (selected: boolean) => {
    setIsShipmentSelected(selected);
  };
  useEffect(() => {
    if (!isMobile) return navigate('/search');
  }, [isMobile, navigate]);

  useEffect(() => {
    return () => {
      setCookie(COOKIE_IDS.SHIPMENT_ID, id?.toString() || '');
    };
  }, []);

  useEffect(() => {
    if (id) {
      const checkDateFromLoadPostings = [
        ...searchResultData?.loadPostings,
        ...watchedLoadsData?.loadPostings,
        ...loadDetails,
      ]?.filter((item: any) => {
        item?.shipmentId === id && setMyloads(true);
        return item?.loadId === id || item?.shipmentId === id;
      })?.[0];
      if (checkDateFromLoadPostings) setData(checkDateFromLoadPostings);
      else if (loadByIdData) {
        setData(loadByIdData);
      } else {
        toast(SERVER_ERROR_MESSAGES?.generalMsg);
        navigate('/');
      }
    }
  }, [id, searchResultData?.loadPostings, watchedLoadsData?.loadPostings, loadByIdData]);

  if (!id) return null;
  const isLastShipment = (lastLocation: any, availableAnchorShipments: any) => {
    return availableAnchorShipments.some(
      (shipment: any) => shipment.shipmentId === lastLocation?.shipmentId,
    );
  };

  const hasAppointment = isAppointmentState(data?.loadStop || data?.shipmentStops);
  const isBinRateAvailable = data?.rate?.totalCharge;
  const payload = {
    driverId: memberData?.member?.userApplicationDetails?.[0].attributes.PrimaryBookingDriverId,
    loadId: data?.loadId,
  };
  /**
   * acceptModalData function returns modal data based on bookLoadData status.
   * @param {any} bookLoadData - Data returned from booking load API call
   * @returns {Object} - Modal data object
   */
  const acceptModalData = (bookLoadData: any) => {
    let acceptModalMessage = bookLoadData?.message || '';
    acceptModalMessage = acceptModalMessage.replace(/\. /g, '.<br />');
    const formatMessage = (message: string) => {
      return message.split('<br />').map((part, index) => (
        <span key={index}>
          {part}
          {index < message.split('<br />').length - 1 && <br />}
        </span>
      ));
    };
    if (bookLoadData?.status === 'SUCCESS') {
      let replacedAcceptModalMessage = acceptModalMessage?.replace(
        /\$1/g,
        data?.loadId || data?.shipmentId,
      );
      fbLogEvent(EVENT.UE_BOOK_NOW_SUCCESS, {
        screen: SCREEN.LOAD_DETAILS_RATE,
        lead: type,
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
      });
      fbLogEvent(EVENT.SV_LOAD_BOOK_SUCCESS, {
        screen: SCREEN.LOAD_BOOK_THANK_YOU,
        lead: type,
        load_id: data?.loadId,
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
      });
      if (selectedAnchor.current) {
        selectedAnchor.current = '';
      }
      return {
        title: 'Thank you',
        icon: '/oo/assets/icon/confirm.svg',
        children: (
          <div className="AcceptSuccessFailureModal">
            <div className="MemberDetails">
              {memberDetails?.map((item, index) => (
                <div key={index}>
                  {item.label !== '' && item.value !== '' && (
                    <div className="Detail_Item">
                      <div className="MemberDetails_Heading">{item.label} :</div>
                      <div className="MemberDetails_Details">{item.value}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="AcceptModalMessage">{formatMessage(replacedAcceptModalMessage)}</div>
          </div>
        ),
        button: [
          {
            title: 'Go Home',
            type: 'secondary-cta-btn',
            onClick: () => {
              navigate('/', { state: { isReload: true } });
              fbLogEvent(EVENT.UE_LOAD_BOOK_GO_HOME, {
                screen: SCREEN.LOAD_BOOK_THANK_YOU,
                lead: type,
                load_id: data?.loadId,
                ...(data?.division && { load_divison: data?.division }),
                ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                ...(data?.rate?.binRateIdentifier && {
                  load_bin_rate_id: data?.rate?.binRateIdentifier,
                }),
                ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
              });
            },
          },
          {
            title: 'Reload Now',
            type: 'primary-cta-btn',
            onClick: () => {
              const load = data?.loadStop?.find(
                (item: any) => item?.stopSequence === data?.numberOfStops,
              );
              const payload = {
                originLocation: {
                  City: load?.city,
                  State: load?.state,
                  Country: load?.country,
                  Zip: load?.postalCode,
                },
                destinationLocation: {},
                originRadius: appConfigData?.ORIGIN_RADIUS_INIT?.initialValue,
                destinationRadius: appConfigData?.DESTINATION_RADIUS_INIT?.initialValue,
                originFromDate: commonDateFormat(
                  isBeforeCurrentDate(load?.appointmentFrom) ? dayjs() : load?.appointmentFrom,
                ) as string,
                originToDate: commonDateFormat(
                  isBeforeCurrentDate(load?.appointmentTo)
                    ? dayjs().add(pickupDateOffset, 'day')
                    : load?.appointmentTo,
                ) as string,
                destinationFromDate: commonDateFormat(dayjs()) as string,
                destinationToDate: commonDateFormat(dayjs().add(dropDateOffset, 'day')) as string,
              };
              dispatch(updateSearchQuery(payload));
              dispatch(handleResetSearchQueryError());
              if (!NewSearchContainer) {
                setThankYouModalProps({ isModalOpen: false });
              } else {
                navigate('/search', { state: { isReload: true } });
              }
              fbLogEvent(EVENT.UE_LOAD_BOOK_RELOAD_NOW, {
                screen: SCREEN.LOAD_BOOK_THANK_YOU,
                lead: type,
                load_id: data?.loadId,
                ...(data?.division && { load_divison: data?.division }),
                ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                ...(data?.rate?.binRateIdentifier && {
                  load_bin_rate_id: data?.rate?.binRateIdentifier,
                }),
                ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
              });
            },
          },
        ],
      };
    } else {
      fbLogEvent(EVENT.UE_BOOK_NOW_FAILURE, {
        screen: SCREEN.LOAD_DETAILS_RATE,
        lead: tabFinding(type),
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
      });
      fbLogEvent(EVENT.SV_LOAD_BOOK_FAIL, {
        screen: SCREEN.LOAD_BOOK_FAIL,
        lead: tabFinding(type),
        AssociatedLoadId: data?.loadId,
        modalTitle: bookLoadData?.modalHeader || BookingFailed,
        failureReason: acceptModalMessage,
        ...(data?.division && { load_divison: data?.division }),
        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
        ...(data?.rate?.binRateIdentifier && {
          load_bin_rate_id: data?.rate?.binRateIdentifier,
        }),
        ...(data?.estimatedTotalRpm && {
          load_estimated_rpm: data?.estimatedTotalRpm,
        }),
      });
      switch (bookLoadData?.reference) {
        case 'NewSearch':
          return {
            title: bookLoadData?.modalHeader || BookingFailed,
            icon: '/oo/assets/icon/info.svg',
            children: (
              <div className="AcceptSuccessFailureModal">
                <div className="MemberDetails">
                  {memberDetails?.map((item, index) => (
                    <div key={index}>
                      {item.label !== '' && item.value !== '' && (
                        <div className="Detail_Item">
                          <div className="MemberDetails_Heading">{item.label} :</div>
                          <div className="MemberDetails_Details">{item.value}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="AcceptModalMessage">{formatMessage(acceptModalMessage)}</div>
              </div>
            ),
            button: [
              {
                title: CANCEL_TEXT,
                type: 'secondary-cta-btn',
                onClick: () => {
                  fbLogEvent(
                    selectedAnchor?.current !== null
                      ? EVENT.UE_LOAD_BOOK_INSERT_SHIPMENT
                      : EVENT.UE_LOAD_BOOK_REFRESH_RESULTS,
                    {
                      screen: SCREEN.LOAD_BOOK_FAIL,
                      lead: tabFinding(type),
                      AssociatedLoadId: data?.loadId,
                      modalTitle: BookingFailed,
                      failureReason: acceptModalMessage,
                      ...(data?.division && { load_divison: data?.division }),
                      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                      ...(data?.rate?.binRateIdentifier && {
                        load_bin_rate_id: data?.rate?.binRateIdentifier,
                      }),
                      ...(data?.estimatedTotalRpm && {
                        load_estimated_rpm: data?.estimatedTotalRpm,
                      }),
                    },
                  );
                  setThankYouModalProps({ isModalOpen: false });
                  if (selectedAnchor.current !== null) {
                    selectedAnchor.current = '';
                  }
                },
              },
              {
                title: RefreshResults,
                type: 'primary-cta-btn',
                onClick: () => {
                  fbLogEvent(EVENT.UE_LOAD_BOOK_REFRESH_RESULTS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: tabFinding(type),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: tabFinding(type),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  if (selectedAnchor.current !== null) {
                    selectedAnchor.current = '';
                  }
                  const payload: any = {
                    size: sortAndFilter?.size,
                    page: sortAndFilter?.page,
                    sortColumn: sortAndFilter?.sortColumn,
                    sortDirection: sortAndFilter?.sortDirection,
                    originCity: searchQuery?.originLocation?.City ?? ANYWHERE,
                    originState: searchQuery?.originLocation?.State ?? '',
                    originRadius: searchQuery?.originRadius,
                    originCountry: searchQuery?.originLocation?.Country ?? 'US',
                    originPostalCode: searchQuery?.originLocation?.Zip ?? '',
                    pickUpStartDate:
                      dataBaseFriendlyDateFormat(searchQuery?.originFromDate, 'startDate', 'GMT') ??
                      null,
                    pickUpEndDate:
                      dataBaseFriendlyDateFormat(searchQuery?.originToDate, 'endDate', 'GMT') ??
                      null,
                    dropStartDate:
                      dataBaseFriendlyDateFormat(
                        searchQuery?.destinationFromDate,
                        'startDate',
                        'GMT',
                      ) || '',
                    dropEndDate:
                      dataBaseFriendlyDateFormat(
                        searchQuery?.destinationToDate,
                        'endDate',
                        'GMT',
                      ) || '',
                    destinationCity: searchQuery?.destinationLocation?.City ?? ANYWHERE,
                    destinationStates: Array.isArray(searchQuery?.destinationLocation?.State)
                      ? searchQuery?.destinationLocation?.State?.filter((item: any) => !!item) || []
                      : [searchQuery?.destinationLocation?.State],
                    destinationCountry: searchQuery?.destinationLocation?.Country ?? '',
                    destinationPostalCode: searchQuery?.destinationLocation?.Zip ?? '',
                    destinationRadius: searchQuery?.destinationRadius,
                    appointmentConfirmed: null,
                    extendedNetwork: null,
                  };
                  dispatch(getSearchLoads({ queryPayloads: payload }));
                  navigate('/search/results');
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
            ],
          };
        case 'NATNAL':
          return {
            title: bookLoadData?.modalHeader || BookingFailed,
            icon: '/oo/assets/icon/info.svg',
            children: (
              <div className="AcceptSuccessFailureModal">
                <div className="MemberDetails">
                  {memberDetails?.map((item, index) => (
                    <div key={index}>
                      {item.label !== '' && item.value !== '' && (
                        <div className="Detail_Item">
                          <div className="MemberDetails_Heading">{item.label} :</div>
                          <div className="MemberDetails_Details">{item.value}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="AcceptModalMessage">{formatMessage(acceptModalMessage)}</div>
              </div>
            ),
            button: [
              {
                title: CANCEL_TEXT,
                type: 'secondary-cta-btn',
                onClick: () => {
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: tabFinding(type),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
              {
                title: UpdateNow,
                type: 'primary-cta-btn',
                onClick: () => {
                  fbLogEvent(EVENT.UE_LOAD_BOOK_UPDATE_NOW, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: tabFinding(type),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  fbLogEvent(EVENT.UE_LOAD_BOOK_DISMISS, {
                    screen: SCREEN.LOAD_BOOK_FAIL,
                    lead: tabFinding(type),
                    AssociatedLoadId: data?.loadId,
                    modalTitle: BookingFailed,
                    failureReason: acceptModalMessage,
                    ...(data?.division && { load_divison: data?.division }),
                    ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                    ...(data?.rate?.binRateIdentifier && {
                      load_bin_rate_id: data?.rate?.binRateIdentifier,
                    }),
                    ...(data?.estimatedTotalRpm && {
                      load_estimated_rpm: data?.estimatedTotalRpm,
                    }),
                  });
                  setThankYouModalProps({ isModalOpen: false });
                  navigate('/natnal');
                },
              },
            ],
          };
        default:
          return {
            title: bookLoadData?.modalHeader || BookingFailed,
            icon: '/oo/assets/icon/info.svg',
            children: (
              <div className="AcceptSuccessFailureModal">
                <div className="MemberDetails">
                  {memberDetails?.map((item, index) => (
                    <div key={index}>
                      {item.label !== '' && item.value !== '' && (
                        <div className="Detail_Item">
                          <div className="MemberDetails_Heading">{item.label} :</div>
                          <div className="MemberDetails_Details">{item.value}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="AcceptModalMessage">
                  {formatMessage(acceptModalMessage) || SERVER_ERROR_MESSAGES?.generalMsg}
                </div>
              </div>
            ),
            button: [
              {
                title: OK_TEXT,
                type: 'primary-cta-btn',
                onClick: () => {
                  setThankYouModalProps({ isModalOpen: false });
                },
              },
            ],
          };
      }
    }
  };
  const footerComponent = () => {
    return (
      <p data-testid="Footer_Component">
        Click to{' '}
        <span
          className="underline CP"
          onClick={() => {
            fbLogEvent(EVENT.UE_LOAD_RATE_INSERT_SHIPMENT, {
              screen: SCREEN.LOAD_DETAILS_RATE,
              lead: tabFinding(type),
              ...(data?.division && { load_divison: data?.division }),
              ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
              ...(data?.rate?.binRateIdentifier && {
                load_bin_rate_id: data?.rate?.binRateIdentifier,
              }),
              ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
            });
            const callback = (res: any) => {
              setBookNowModalProps({ isModalOpen: false });
              const modalProps = {
                isModalOpen: true,
                title: `Load Sequence`,
                titleIcon: '/oo/assets/icon/info.svg',
                contentAlignment: 'left',
                additionalCssClass: 'InsertShipmentModal action-button-container contents button',
                buttons: [
                  {
                    title: CANCEL_TEXT,
                    type: 'secondary-cta-btn',
                    onClick: () => {
                      fbLogEvent(EVENT.UE_LOAD_SEQUENCE_CANCEL, {
                        screen: SCREEN.LOAD_DETAILS_LOAD_SEQUENCE,
                        lead: tabFinding(type),
                        ...(data?.division && { load_divison: data?.division }),
                        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                        ...(data?.rate?.binRateIdentifier && {
                          load_bin_rate_id: data?.rate?.binRateIdentifier,
                        }),
                        ...(data?.estimatedTotalRpm && {
                          load_estimated_rpm: data?.estimatedTotalRpm,
                        }),
                      });
                      selectedAnchor.current = null;
                      handleShipmentSelection(false);
                      setInsertShipmentModalProps({ isModalOpen: false });
                      proceedWithBooking();
                    },
                  },
                  {
                    title: 'Accept',
                    type: `primary-cta-btn`,
                    isDisabled: !isShipmentSelected,
                    onClick: () => {
                      fbLogEvent(EVENT.UE_LOAD_SEQUENCE_ACCEPT, {
                        screen: SCREEN.LOAD_DETAILS_LOAD_SEQUENCE,
                        insertedShipmentId: selectedAnchor,
                        lead: tabFinding(type),
                        ...(data?.division && { load_divison: data?.division }),
                        ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                        ...(data?.rate?.binRateIdentifier && {
                          load_bin_rate_id: data?.rate?.binRateIdentifier,
                        }),
                        ...(data?.estimatedTotalRpm && {
                          load_estimated_rpm: data?.estimatedTotalRpm,
                        }),
                      });
                      let _payload: any = { ...payload };
                      if (selectedAnchor?.current) {
                        _payload = {
                          ...payload,
                          anchorShipmentID: selectedAnchor?.current,
                          anchorDirection: 'ASSIGN DRIVER AFTER',
                        };
                      }
                      dispatch(bookLoad(_payload)).then((res) => {
                        setBookNowModalProps({ isModalOpen: false });
                        selectedAnchor.current = '';
                        handleShipmentSelection(false);
                        setInsertShipmentModalProps({ isModalOpen: false });
                        handleAcceptClick(res.payload);
                      });
                    },
                  },
                ],
              };
              setInsertShipmentModalProps({ ...modalProps });
              fbLogEvent(EVENT.SV_VIEW_BOOK_NOW_LOAD_SEQUENCE, {
                screen: SCREEN.LOAD_DETAILS_LOAD_SEQUENCE,
                loadId: data?.loadId,
                lead: tabFinding(type),
                ...(data?.division && { load_divison: data?.division }),
                ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
                ...(data?.rate?.binRateIdentifier && {
                  load_bin_rate_id: data?.rate?.binRateIdentifier,
                }),
                ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
              });
            };
            dispatch(
              getAvailableAnchorShipments({
                payload: {
                  driverId:
                    memberData?.member?.userApplicationDetails?.[0].attributes
                      .PrimaryBookingDriverId,
                },
                callback: callback,
              }),
            );
          }}
        >
          Insert Shipment
        </span>
      </p>
    );
  };
  const proceedWithBooking = () => {
    fbLogEvent(EVENT.SV_VIEW_BOOK_NOW_RATE, {
      screen: SCREEN.LOAD_DETAILS_RATE,
      loadId: data?.loadId,
      lead: type,
      ...(data?.division && { load_divison: data?.division }),
      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
      ...(data?.rate?.binRateIdentifier && {
        load_bin_rate_id: data?.rate?.binRateIdentifier,
      }),
      ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
    });
    const modalProps = {
      isModalOpen: true,
      title: `Rate Details of ${data?.loadId}`,
      titleIcon: '/oo/assets/icon/info.svg',
      contentAlignment: 'center',
      additionalCssClass: ' bookNow-modal-mobile button contents BookNow_Modal Table_Container',
      children: (
        <>
          <LoadRateContainer tableTitle={''} rate={data?.rate} isMemberDetailVisible={true} />
          {data?.extendedNetwork && (
            <div className="BookingModal_ExtendedNetwork_Indicator">Extended Network</div>
          )}
          <TermsAndConditions showToggle={true} />
        </>
      ),
      buttons: [
        {
          title: 'Decline',
          type: 'secondary-cta-btn',
          onClick: () => {
            fbLogEvent(EVENT.UE_LOAD_RATE_DECLINE, {
              screen: SCREEN.LOAD_DETAILS_RATE,
              lead: type,
              ...(data?.division && { load_divison: data?.division }),
              ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
              ...(data?.rate?.binRateIdentifier && {
                load_bin_rate_id: data?.rate?.binRateIdentifier,
              }),
              ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
            });
            setBookNowModalProps({ isModalOpen: false });
          },
        },
        {
          title: 'Accept',
          type: 'primary-cta-btn',
          onClick: () => {
            fbLogEvent(EVENT.UE_LOAD_RATE_ACCEPT, {
              screen: SCREEN.LOAD_DETAILS_RATE,
              lead: type,
              ...(data?.division && { load_divison: data?.division }),
              ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
              ...(data?.rate?.binRateIdentifier && {
                load_bin_rate_id: data?.rate?.binRateIdentifier,
              }),
              ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
            });
            dispatch(bookLoad(payload)).then((res) => {
              selectedAnchor.current = null;
              setBookNowModalProps({ isModalOpen: false });
              handleAcceptClick(res.payload);
            });
          },
        },
      ],
      footer:
        memberData?.member?.userApplicationDetails?.[0].attributes.LineOfService !== 'BULK' &&
          !data?.extendedNetwork
          ? footerComponent()
          : null,
    };
    fbLogEvent(EVENT.UE_BOOK_LOAD_NOW, {
      screen: SCREEN.SEARCH,
      tab: type,
      loadId: data?.loadId,
      ...(data?.division && { load_divison: data?.division }),
      ...(data?.rate?.binSources && { load_source_tms: data?.rate?.binSource }),
      ...(data?.rate?.binRateIdentifier && {
        load_bin_rate_id: data?.rate?.binRateIdentifier,
      }),
      ...(data?.estimatedTotalRpm && { load_estimated_rpm: data?.estimatedTotalRpm }),
    });
    setBookNowModalProps(modalProps);
  };
  /**
   * handleBookNowClick function sets modal props for book now modal and logs event.
   */
  const handleBookNowClick = () => {
    const originLoadStop = data?.loadStop?.find((item: any) => item?.stopSequence === 1);
    const originAppointmentFrom = commonDateFormat(
      originLoadStop?.appointmentFrom,
      false,
      false,
      originLoadStop?.timeZone ? originLoadStop?.timeZone : null,
    );
    const isToday = dayjs(originAppointmentFrom).isSame(dayjs(), 'day');
    if (isToday) {
      const temp = {
        isModalOpen: true,
        title: `Warning`,
        titleIcon: '/oo/assets/icon/alertInTriangle.svg',
        contentAlignment: 'center',
        message:
          "<div class='Warning_Modal_Text'><h3 class='Warning_Modal_Regular' >This route has the following warnings. Please confirm that you move the load by clicking Accept. </h3><div><h2 class='Warning_Modal_Medium'>This route picks up today.</h2></div></div>",
        additionalCssClass: 'additional-css Warning_Modal',

        buttons: [
          {
            title: CANCEL_TEXT,
            type: 'secondary-cta-btn',
            onClick: () => {
              setWarningModalProps({ isModalOpen: false });
            },
          },
          {
            title: 'Accept',
            type: 'primary-cta-btn',
            onClick: () => {
              setWarningModalProps({ isModalOpen: false });
              proceedWithBooking();
            },
          },
        ],
      };
      setWarningModalProps(temp);
    } else {
      proceedWithBooking();
    }
  };
  /**
   * handleAcceptClick function sets modal props for thank you modal.
   * @param {any} bookLoadData - Data returned from booking load API call
   */
  const handleAcceptClick = (bookLoadData: any) => {
    const modalData = acceptModalData(bookLoadData);
    const modalProps = {
      isModalOpen: true,
      title: modalData?.title,
      titleIcon: modalData?.icon,
      children: modalData?.children,
      buttons: modalData?.button,
      contentScroll: false,
      additionalCssClass: 'Thankyou_Modal',
    };
    setThankYouModalProps(modalProps);
  };
  const handleLastShipmentModal = () => {
    const modalProps = {
      isModalOpen: true,
      title: availableAnchorShipments[0].shipmentId,
      titleIcon: '/oo/assets/icon/info.svg',
      size: 'small-height',
      message: `<div class='my-3 px-2 py-2'><span>${LAST_SHIPMENT_MODAL_MESSAGE}</span ></div>`,

      buttons: [
        {
          title: OK_TEXT,
          type: 'primary-cta-btn',
          onClick: () => {
            setLastShipmentWarningModal({ isModalOpen: false });
          },
        },
      ],
    };
    setLastShipmentWarningModal(modalProps);
  };
  return (
    <>
      <CustomModal {...bookNowModalProps} />
      <CustomModal {...thankYouModalProps} />
      <CustomModal {...warningModalProps} />
      <CustomModal {...referencesModalProps} />
      <CustomModal {...lastShipmentWarningModal} />
      <CustomModal {...insertShipmentModalProps}>
        <div className="InsertShipmentModalContainer">
          <div className="NewLoadContainer">
            <p className="NewLoadContainerHeading">New Load</p>
            <LoadCard
              key={data?.loadId}
              data={data}
              hideRightTopPart={true}
              size={ELoadCardSize.small}
              isSearchResults={type === 'new'}
              isRecent={type === 'recent'}
              isFavorite={type === 'favorite'}
              isWatchedLoads={type === 'watched'}
              hideSpecialServices={true}
              showLoadIdLeftMobile={true}
              showRateLeft={true}
              hideRightPart={true}
              hideSpecialInfoLeft={true}
              hideDeadHead={true}
              additionalCssClass="InsertShipmentLoadCardPadding"
            />
          </div>
          <div className="NextShipmentModalContainer">
            <p className="NextShipmentModalContainerHeading">
              Select shipment new load comes after
            </p>
            <div className="NextShipmentContainer">
              {availableAnchorShipments.length > 0 &&
                availableAnchorShipments?.map((item: any, index?: number) => (
                  <LoadCard
                    key={item?.shipmentId}
                    data={item}
                    withoutDateTime={false}
                    withoutYear={true}
                    isInsertShipment={true}
                    size={ELoadCardSize.small}
                    cardClickEnabled={true}
                    additionalCssClass={`${item?.shipmentId === selectedAnchor?.current ? 'anchorShipment' : ''
                      } ${'InsertShipmentLoadCardPadding'} ${isLastShipment(lastLocation, availableAnchorShipments) && item?.shipmentId === lastLocation?.shipmentId ? 'disabled' : ''}`}
                    handleCardClick={(id) => {
                      if (
                        isLastShipment(lastLocation, availableAnchorShipments) &&
                        item?.shipmentId === lastLocation?.shipmentId
                      ) {
                        selectedAnchor.current = null;
                        handleShipmentSelection(false);
                        handleLastShipmentModal();
                      } else {
                        if (id === selectedAnchor?.current) {
                          selectedAnchor.current = '';
                          handleShipmentSelection(false);
                        } else {
                          selectedAnchor.current = id || '';
                          handleShipmentSelection(true);
                        }
                      }
                      setInsertShipmentModalProps((prevProps: any) => ({
                        ...prevProps,
                        buttons: prevProps?.buttons?.map((btn: any) =>
                          btn?.title === 'Accept'
                            ? { ...btn, isDisabled: !selectedAnchor.current }
                            : btn,
                        ),
                      }));
                    }}
                    hideRightTopPart={true}
                    isSearchResults={type === 'new'}
                    isRecent={type === 'recent'}
                    isFavorite={type === 'favorite'}
                    isWatchedLoads={type === 'watched'}
                    isMyLoads={isMyloads}
                    hideSpecialServices={true}
                    showLoadIdLeftMobile={true}
                    hideSpecialInfoLeft={true}
                    hideDeadHead={true}
                    hideRightPart={true}
                  />
                ))}
            </div>
          </div>
        </div>
      </CustomModal>

      <div className={`ExpandedResults_Container${showBanner ? ' AlertBannerVisible' : ''}`}>
        {data && (
          <div className="Map_Section">
            <LoadMapContainer data={data} showZoomControls={false} showExpandedView={true} />
          </div>
        )}
        <LoadCard
          data={data}
          isExpanded={true}
          showBottomButton={false}
          size={ELoadCardSize.small}
          isMyLoads={isMyloads}
          isRecent={tabFinding(type) === 'recent'}
          isFavorite={tabFinding(type) === 'favorite'}
          isSearchResults={tabFinding(type) === 'new'}
          isWatchedLoads={tabFinding(type) === 'watched'}
          isGlobalSearch={loadByIdData}
          hideRightTopPart={true}
          showNewUpdateFlag={true}
        />
        <div className="Booking_Terms">
          {!isMyloads && <TermsAndConditions showToggle={true} />}
          {isMyloads && data?.shipmentAndStopReferences.length > 0 && (
            <div>
              <button
                className="References_Btn"
                onClick={() => {
                  setReferencesModalProps({
                    isModalOpen: true,
                    title: 'References',
                    closeModalAction: () => setReferencesModalProps({ isModalOpen: false }),
                    contentAlignment: 'left',
                    backdropDismiss: true,
                    additionalCssClass: ' additional-css commodities-modal',
                    contentScroll: 'deny',
                    children: (
                      <LoadReferencesContainer references={data?.shipmentAndStopReferences} />
                    ),
                  });
                }}
              >
                References
              </button>
            </div>
          )}
          {isMobile && <FloatingBtnContainer data={data} isMyLoads={isMyloads} />}
        </div>
        {hasAppointment && isBinRateAvailable && (
          <div className="BookNow_Button_Container" data-testid="BookNowBtn">
            <button
              className="primary-cta-btn"
              onClick={() => {
                handleBookNowClick();
              }}
            >
              Book Now
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ExpandedResults;
